import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { CategorySection } from "./CategorySection";
import {
  CRITERIA_GROUPS,
  MOCK_EVALUATORS,
  type Candidate,
  type ScoreMap,
  type CriterionKey,
  type EvaluatorName,
} from "./mockSelectionData";

// The active evaluator is always "Selector A" in the demo — the form represents
// their session. Other evaluators' scores are read-only (visible in Tab 2).
const ACTIVE_EVALUATOR: EvaluatorName = "Selector A";

interface Props {
  candidates: Candidate[];
  selectedCandidateId: number;
  onSelectCandidate: (id: number) => void;
  scores: ScoreMap;
  onUpdateScore: (candidateId: number, evaluator: EvaluatorName, key: CriterionKey, value: number) => void;
}

function initScores(candidateId: number, scores: ScoreMap): Record<string, number> {
  return Object.fromEntries(
    Object.entries(scores[candidateId][ACTIVE_EVALUATOR]).map(([k, v]) => [k, v])
  );
}

// Unique categories to render (Core Skills appears as 3 sub-groups, show once per sub)
const SECTIONS = CRITERIA_GROUPS;

export function SelectorEvaluationTab({
  candidates,
  selectedCandidateId,
  onSelectCandidate,
  scores,
  onUpdateScore,
}: Props) {
  const { toast } = useToast();

  const [localScores, setLocalScores] = useState<Record<string, number>>(
    () => initScores(selectedCandidateId, scores)
  );
  const [justifications, setJustifications] = useState<Record<string, string>>({});

  // Reset local state when candidate changes
  useEffect(() => {
    setLocalScores(initScores(selectedCandidateId, scores));
    setJustifications({});
  }, [selectedCandidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleScoreChange(key: string, value: number) {
    setLocalScores((prev) => ({ ...prev, [key]: value }));
  }

  function handleJustificationChange(key: string, value: string) {
    setJustifications((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    // Flush local scores to page-level state
    Object.entries(localScores).forEach(([key, value]) => {
      onUpdateScore(selectedCandidateId, ACTIVE_EVALUATOR, key as CriterionKey, value);
    });
    const candidate = candidates.find((c) => c.id === selectedCandidateId);
    toast({
      title: "Evaluation saved",
      description: `${ACTIVE_EVALUATOR}'s scores for ${candidate?.name ?? "candidate"} recorded.`,
    });
  }

  const candidate = candidates.find((c) => c.id === selectedCandidateId);
  const totalScored = Object.values(localScores).length;
  const avgScore =
    Object.values(localScores).reduce((a, b) => a + b, 0) / (totalScored || 1);

  return (
    <div className="space-y-5" data-testid="selector-evaluation-tab">
      {/* Candidate switcher + hero */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="text-xs text-gray-500 mb-1">Evaluating as: <span className="font-medium text-gray-700">{ACTIVE_EVALUATOR}</span></p>
          <Select
            value={String(selectedCandidateId)}
            onValueChange={(v) => onSelectCandidate(Number(v))}
          >
            <SelectTrigger className="w-full sm:w-72" data-testid="candidate-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name} — {c.position}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {candidate && (
          <div className="flex items-center gap-3 shrink-0">
            {/* Photo placeholder */}
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-lg">
              {candidate.name[0]}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{candidate.name}</p>
              <div className="flex gap-1.5 mt-0.5">
                <Badge variant="secondary" className="text-xs">{candidate.ageGroup}</Badge>
                <Badge variant="outline" className="text-xs">{candidate.position}</Badge>
              </div>
            </div>
            <div className="ml-4 text-center hidden md:block">
              <p className="text-2xl font-bold text-gray-900">{avgScore.toFixed(1)}</p>
              <p className="text-xs text-gray-400">avg / 5</p>
            </div>
          </div>
        )}
      </div>

      {/* 4 category sections — all collapsed by default */}
      <div className="space-y-3" data-testid="category-sections">
        {SECTIONS.map((section, i) => (
          <CategorySection
            key={`${section.category}-${section.subcategory ?? i}`}
            category={section.category}
            subcategory={section.subcategory}
            color={section.color}
            criteria={section.criteria}
            scores={localScores}
            onScoreChange={handleScoreChange}
            justifications={justifications}
            onJustificationChange={handleJustificationChange}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-gray-400">
          {MOCK_EVALUATORS.length} selectors · {totalScored} criteria scored
        </p>
        <Button
          className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
          onClick={handleSave}
          data-testid="btn-save-evaluation"
        >
          Save Evaluation
        </Button>
      </div>
    </div>
  );
}
